import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ClientPicker from "../ClientPicker";
import AddressAutocomplete from "../AddressAutocomplete";
import { ContractScheduleFields } from "./ContractScheduleFields";
import { ContractTeamFields } from "./ContractTeamFields";
import { todayIso } from "./contractUtils";

const CATEGORIES = ["cleaning", "maintenance", "planned_works", "inspection", "landscaping", "electrical", "plumbing", "heating", "general", "other"];

export const emptyContract = () => ({
  title: "", description: "", category: "cleaning",
  client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "",
  property_id: "", property_name: "", add_to_portfolio: false, street_address: "", town_city: "", county: "", postcode: "", latitude: null, longitude: null,
  site_contact_name: "", site_contact_phone: "", site_contact_email: "",
  frequency: "weekly", weekdays: [0, 1, 2, 3, 4], month_day: 1, start_date: todayIso(), end_date: "", start_time: "08:00", end_time: "",
  default_assignee_type: "", default_operative_id: "", default_operative_ids: [], default_vendor_id: "",
  rate: "", rate_basis: "visit", operatives_per_visit: 1, client_po_number: "", invoicing_mode: "per_visit", client_can_book: true, min_notice_days: 1, short_notice_days: 3,
  instructions: "", show_contractor_to_client: null,
});

export const contractToForm = (c) => ({ ...emptyContract(), ...Object.fromEntries(Object.entries(c).filter(([k]) => k in emptyContract())), month_day: c.month_day || 1,
  default_operative_id: c.default_operative_id || "", default_operative_ids: c.default_operative_ids || (c.default_operative_id ? [c.default_operative_id] : []), default_vendor_id: c.default_vendor_id || "", rate: c.rate ?? "", rate_basis: c.rate_basis || "visit", operatives_per_visit: c.operatives_per_visit || 1 });

/** Create / edit form. onSubmit(payload) must return a promise. */
export function ContractForm({ initial, onSubmit, onCancel, submitLabel = "Create Contract", isEdit = false }) {
  const [form, setForm] = useState(initial || emptyContract());
  const [props, setProps] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!form.client_id) { setProps([]); return; }
    API.get(`/clients/${form.client_id}/properties`).then(({ data }) => setProps(data.properties || [])).catch(() => setProps([]));
  }, [form.client_id]);

  const pickProperty = (pid) => {
    const p = props.find((x) => x.id === pid);
    if (!p) { set({ property_id: "" }); return; }
    set({ property_id: p.id, property_name: p.name || "", street_address: p.address_line || "", town_city: p.town_city || "", county: p.county || "", postcode: p.postcode || "",
      site_contact_name: p.site_contact_name || form.site_contact_name, site_contact_phone: p.site_contact_phone || form.site_contact_phone, site_contact_email: p.site_contact_email || form.site_contact_email });
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Give the contract a title");
    if (!form.client_id) return toast.error("Select a client");
    if (!form.street_address || !form.town_city || !form.postcode) return toast.error("Street, town and postcode are required");
    if (!form.start_date) return toast.error("Pick a start date");
    if (["daily", "weekly", "fortnightly"].includes(form.frequency) && !form.weekdays.length) return toast.error("Pick at least one visit day");
    setSaving(true);
    try {
      await onSubmit({ ...form, rate: Number(form.rate || 0), operatives_per_visit: Math.max(1, Number(form.operatives_per_visit || 1)), month_day: Number(form.month_day || 1), min_notice_days: Number(form.min_notice_days || 0), short_notice_days: Number(form.short_notice_days || 0) });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the contract"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="contract-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><Label className="text-xs">Contract title *</Label><Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Daily office clean — Head Office" data-testid="contract-title" /></div>
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={form.category} onValueChange={(v) => set({ category: v })}><SelectTrigger data-testid="contract-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <div><Label className="text-xs">Scope / description</Label><Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="What's covered on each visit — shown to the client" data-testid="contract-description" /></div>

      <div className="border border-border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold font-[Chivo]">Client & property</h3>
        <ClientPicker value={form} onChange={(next) => setForm(next)} testIdPrefix="contract-client" />
        {props.length > 0 && (
          <div>
            <Label className="text-xs">Client property</Label>
            <Select value={form.property_id || "__none__"} onValueChange={(v) => pickProperty(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="contract-property"><SelectValue placeholder="Pick a saved property (fills the address)" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">Enter address manually</SelectItem>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.address_line} · {p.postcode}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {form.client_id && !form.property_id && !isEdit && (
          <p className="text-[11px] text-muted-foreground" data-testid="contract-auto-portfolio-note">New address? It's saved to the client's portfolio automatically so it can be picked for future requests, jobs and contracts.</p>
        )}
        <AddressAutocomplete label="Search address" placeholder="Start typing address or postcode…" onSelect={(a) => set({ street_address: a.street_address || form.street_address, town_city: a.town_city || form.town_city, county: a.county || form.county, postcode: a.postcode || form.postcode, latitude: a.latitude || null, longitude: a.longitude || null })} data-testid="contract-address-autocomplete" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Label className="text-xs">Property name</Label><Input value={form.property_name} onChange={(e) => set({ property_name: e.target.value })} data-testid="contract-prop-name" /></div>
          <div><Label className="text-xs">Street *</Label><Input value={form.street_address} onChange={(e) => set({ street_address: e.target.value })} data-testid="contract-street" /></div>
          <div><Label className="text-xs">Town / city *</Label><Input value={form.town_city} onChange={(e) => set({ town_city: e.target.value })} data-testid="contract-town" /></div>
          <div><Label className="text-xs">Postcode *</Label><Input value={form.postcode} onChange={(e) => set({ postcode: e.target.value })} data-testid="contract-postcode" /></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Site contact</Label><Input value={form.site_contact_name} onChange={(e) => set({ site_contact_name: e.target.value })} data-testid="contract-site-contact" /></div>
          <div><Label className="text-xs">Site phone</Label><Input value={form.site_contact_phone} onChange={(e) => set({ site_contact_phone: e.target.value })} data-testid="contract-site-phone" /></div>
          <div><Label className="text-xs">Site email</Label><Input value={form.site_contact_email} onChange={(e) => set({ site_contact_email: e.target.value })} data-testid="contract-site-email" /></div>
        </div>
      </div>

      <div className="border border-border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold font-[Chivo]">Schedule</h3>
        <ContractScheduleFields form={form} set={set} />
        {isEdit && <p className="text-[11px] text-amber-700 dark:text-amber-400">Changing the pattern re-plans future scheduled visits. Dates booked by hand or by the client are kept.</p>}
      </div>

      <div className="border border-border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold font-[Chivo]">Team, rate & invoicing</h3>
        <ContractTeamFields form={form} set={set} />
      </div>

      <div>
        <Label className="text-xs">Contractor names in the client portal</Label>
        <Select value={form.show_contractor_to_client === true ? "show" : form.show_contractor_to_client === false ? "hide" : "default"} onValueChange={(v) => set({ show_contractor_to_client: v === "show" ? true : v === "hide" ? false : null })}>
          <SelectTrigger data-testid="contract-show-contractor"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Follow the client's default setting</SelectItem>
            <SelectItem value="show">Show — name the in-house operative / supplier in the Team box</SelectItem>
            <SelectItem value="hide">Hide — show "[client's branded name] team" only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Instructions for the team</Label><Textarea rows={3} value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} placeholder="Access codes, alarm, where to sign in…" data-testid="contract-instructions" /></div>

      <div className="flex justify-end gap-2">
        {onCancel && <Button variant="ghost" onClick={onCancel} data-testid="contract-cancel-btn">Cancel</Button>}
        <Button onClick={submit} disabled={saving} data-testid="contract-submit-btn">{saving ? "Saving…" : submitLabel}</Button>
      </div>
    </div>
  );
}
